import compatibility from '../backend/client-compatibility.json';

export const CLIENT_VERSION = compatibility.minimumVersion;
export const CLIENT_VERSION_HEADER = 'X-Mors-Client-Version';

export interface AppVersion {
  version?: string;
  buildTime?: string;
  gitSha?: string;
  minimumClientVersion?: number;
  force?: boolean;
}

export type AppUpdateState = 'ready' | 'updating' | 'failed';
let state: AppUpdateState = 'ready';
let requiredVersion = 0;
let checking: Promise<AppVersion | null> | null = null;
const listeners = new Set<(state: AppUpdateState) => void>();

const setState = (next: AppUpdateState): void => {
  state = next;
  listeners.forEach((listener) => listener(next));
};

export function onAppUpdate(listener: (state: AppUpdateState) => void): () => void {
  listeners.add(listener);
  listener(state);
  return () => { listeners.delete(listener); };
}

export function needsMandatoryUpdate(remote: AppVersion, currentVersion = CLIENT_VERSION): boolean {
  return Number.isSafeInteger(remote.minimumClientVersion) && (remote.minimumClientVersion ?? 0) > currentVersion;
}

/** Сначала убеждаемся, что свежие assets доступны; 503/offline не вызывают reload. */
export async function checkAppVersion(fetcher: typeof fetch = fetch): Promise<AppVersion | null> {
  if (checking) return checking;
  checking = (async () => {
    try {
      const response = await fetcher('/version.json', { cache: 'no-store', signal: AbortSignal.timeout(10_000) });
      if (!response.ok) return null;
      const remote = await response.json() as AppVersion;
      if (typeof remote?.buildTime !== 'string') return null;
      if (needsMandatoryUpdate(remote)) {
        requiredVersion = Math.max(requiredVersion, remote.minimumClientVersion ?? 0);
        setState('updating');
      }
      if (requiredVersion > CLIENT_VERSION && (remote.minimumClientVersion ?? 0) >= requiredVersion) {
        // При ошибке раздачи assets не загоняем пользователя в бесконечные reload.
        const key = 'mors.app-update.reload';
        const target = `${requiredVersion}:${remote.buildTime}`;
        try {
          if (sessionStorage.getItem(key) === target) {
            setState('failed');
            return remote;
          }
          sessionStorage.setItem(key, target);
        } catch {
          // Без доступного sessionStorage автоматическая перезагрузка небезопасна для UX.
          setState('failed');
          return remote;
        }
        window.location.reload();
      }
      return remote;
    } catch {
      return null;
    }
  })().finally(() => { checking = null; });
  return checking;
}

export function requireAppUpdate(minimumVersion: unknown): void {
  if (typeof minimumVersion !== 'number' || !Number.isSafeInteger(minimumVersion) || minimumVersion <= CLIENT_VERSION) {
    setState('failed');
    return;
  }
  requiredVersion = Math.max(requiredVersion, minimumVersion);
  setState('updating');
  void checkAppVersion();
}

export function startAppVersionPolling(onVersion?: (remote: AppVersion) => void): () => void {
  // checkAppVersion дедупит параллельные запросы, поэтому focus+visibilitychange не дают двойной fetch.
  const check = (): void => {
    if (document.visibilityState !== 'visible') return;
    void checkAppVersion().then((remote) => {
      if (remote) onVersion?.(remote);
    });
  };
  check();
  const interval = window.setInterval(check, 60_000);
  window.addEventListener('focus', check);
  document.addEventListener('visibilitychange', check);
  return () => {
    clearInterval(interval);
    window.removeEventListener('focus', check);
    document.removeEventListener('visibilitychange', check);
  };
}

// Новый service worker может определить версию даже у уже открытых вкладок.
if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
    if (event.data?.type === 'mors:client-version') event.ports[0]?.postMessage({ version: CLIENT_VERSION });
  });
}
